import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError, from, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { environment } from '../../../environments/environment';
import { Page } from '../../models/page-wrapper.model';
import { PickerRecentCacheService } from '../../shared/services/picker-recent-cache.service';
import { EntityDropdownPickerAdapter } from '../../shared/components/entity-dropdown-picker/entity-dropdown-picker.model';
import { PickerRecentRecord } from '../../shared/components/entity-dropdown-picker/picker-recent.model';
import { ItemCategory } from './item.model';

@Injectable({
  providedIn: 'root',
})
export class ItemCategoryService {
  private apiUrl = `${environment.restApiUrl}item-categories`;
  private readonly pickerEntityKey = 'item-category';

  constructor(
    private http: HttpClient,
    private recentPickerCache: PickerRecentCacheService
  ) {}

  private handleError(error: HttpErrorResponse) {
    if (error.error instanceof ErrorEvent) {
      console.error('An error occurred:', error.error.message);
    } else {
      console.error(`Backend returned code ${error.status}, body was: ${JSON.stringify(error.error)}`);
    }
    return throwError(() => new Error('Something went wrong; please try again later.'));
  }

  getItemCategoryById(id: number): Observable<ItemCategory> {
    return this.http.get<ItemCategory>(`${this.apiUrl}/${id}`).pipe(catchError(this.handleError));
  }

  loadRecentItemCategories(limit = 10): Observable<ItemCategory[]> {
    return from(this.recentPickerCache.loadRecent<ItemCategory>(this.pickerEntityKey, limit));
  }

  getFreshItemCategoryById(id: number | string): Observable<ItemCategory> {
    return this.getItemCategoryById(Number(id));
  }

  rememberItemCategory(category: ItemCategory): Observable<void> {
    const record: PickerRecentRecord<ItemCategory> = {
      key: `${this.pickerEntityKey}::${category.id}`,
      entityKey: this.pickerEntityKey,
      userKey: this.getCurrentUserKey(),
      entityId: category.id as number,
      label: this.getLabel(category),
      payload: category,
      lastUsedAt: Date.now(),
    };

    return from(this.recentPickerCache.remember(record));
  }

  createItemCategoryPickerAdapter(): EntityDropdownPickerAdapter<ItemCategory> {
    return {
      entityKey: this.pickerEntityKey,
      loadRecent: (limit?: number) => this.loadRecentItemCategories(limit),
      search: (query: string, page: number, size: number) => this.searchItemCategories(query, page, size),
      getFreshById: (id: number | string) => this.getFreshItemCategoryById(id),
      rememberSelection: (category: ItemCategory) => this.rememberItemCategory(category),
      getId: (category: ItemCategory) => category.id!,
      getLabel: (category: ItemCategory) => this.getLabel(category),
      getSecondaryLabel: (category: ItemCategory) => category.code ? `Code: ${category.code}` : '',
      filterBy: 'name,code',
    };
  }

  private searchItemCategories(query: string, page = 0, size = 25): Observable<Page<ItemCategory>> {
    return forkJoin([
      this.searchItemCategoriesByField('name', query, page, size),
      this.searchItemCategoriesByField('code', query, page, size),
    ]).pipe(
      map(([namePage, codePage]) => this.mergeItemCategoryPages(namePage, codePage, page, size)),
      catchError(this.handleError)
    );
  }

  private searchItemCategoriesByField(
    field: 'name' | 'code',
    query: string,
    page: number,
    size: number
  ): Observable<Page<ItemCategory>> {
    const params = {
      [field]: query,
      page,
      size,
    };

    return this.http.get<Page<ItemCategory>>(this.apiUrl, { params }).pipe(
      catchError(() => of(this.emptyItemCategoryPage(page, size)))
    );
  }

  private mergeItemCategoryPages(
    namePage: Page<ItemCategory>,
    codePage: Page<ItemCategory>,
    page: number,
    size: number
  ): Page<ItemCategory> {
    const merged = new Map<number, ItemCategory>();
    [...(namePage.content || []), ...(codePage.content || [])].forEach((category) => {
      if (category?.id !== undefined && category?.id !== null) {
        merged.set(category.id, category);
      }
    });

    const content = Array.from(merged.values()).slice(0, size);
    return {
      ...namePage,
      content,
      number: page,
      size,
      totalElements: merged.size,
      numberOfElements: content.length,
    };
  }

  private emptyItemCategoryPage(page: number, size: number): Page<ItemCategory> {
    return {
      content: [],
      pageable: {
        pageNumber: page,
        pageSize: size,
        sort: { sorted: false, unsorted: true, empty: true },
        offset: page * size,
        paged: true,
        unpaged: false,
      },
      last: true,
      totalPages: 0,
      totalElements: 0,
      size,
      number: page,
      sort: { sorted: false, unsorted: true, empty: true },
      first: page === 0,
      numberOfElements: 0,
      empty: true,
    };
  }

  private getLabel(category: ItemCategory): string {
    return `${category.name ?? ''}${category.code ? ` - ${category.code}` : ''}`.trim();
  }

  private getCurrentUserKey(): string {
    try {
      const rawUser = sessionStorage.getItem('appUser');
      if (!rawUser) {
        return 'anonymous';
      }

      const parsed = JSON.parse(rawUser);
      return String(parsed?.id ?? 'anonymous');
    } catch {
      return 'anonymous';
    }
  }
}
